// DEV-401 public checkout confirm wire client.
//
// The customer card flow deliberately does not call /confirm; it proceeds from
// acceptance directly to /payment. This isolated client pins compatibility for
// any retained confirm caller without changing that zero-/confirm flow.

import {
  CHECKOUT_STAGE2_API_BASE_URL,
  CheckoutStage2ApiError,
  isValidCheckoutSessionPublicId,
} from "./stage2Api";
import { isValidExpectedQuoteHash } from "./paymentApi";
import { appendCheckoutFlowVersion } from "./checkoutFlowVersion";
import { assertCheckoutMutationAllowed } from "./productionMode";

export interface ConfirmCheckoutRequest {
  expectedQuoteHash: string;
  acceptancePublicId?: string;
}

/** Existing paid/order-first response. Keep this byte-compatible. */
export interface PayableConfirmCheckoutResponse {
  checkoutSessionPublicId: string;
  nextAction: "view_order";
  orderKey: string;
  orderCreated?: boolean;
  orderCount?: number;
  invoiceStatus: "not_created";
  correlationId?: string;
  [key: string]: unknown;
}

/**
 * Explicit server-authorised zero-cost outcome. It is intentionally not
 * represented as paid and it never authorises a client-side payment retry.
 */
export interface ComplimentaryConfirmCheckoutResponse {
  checkoutSessionPublicId: string;
  orderKey: string;
  orderStatus?: "complimentary" | string;
  quoteStatus?: string;
  nextAction: "view_order" | "view_access" | "wait_for_release" | "contact_support" | "confirm";
  commercialState: "complimentary";
  paymentRequired: false;
  invoiceRequired: false;
  invoiceStatus: "not_required";
  paymentStatus: "not_required";
  release?: {
    state?: "released" | "pending" | "blocked" | string;
    status?: "released" | "access_released" | "pending" | "blocked" | string;
    reason?: string;
  };
  correlationId?: string;
  [key: string]: unknown;
}

export type ConfirmCheckoutResponse =
  | PayableConfirmCheckoutResponse
  | ComplimentaryConfirmCheckoutResponse;

export function isComplimentaryConfirmResponse(
  response: ConfirmCheckoutResponse | null | undefined,
): response is ComplimentaryConfirmCheckoutResponse {
  return (
    !!response &&
    response.commercialState === "complimentary" &&
    response.paymentRequired === false &&
    response.invoiceRequired === false &&
    response.paymentStatus === "not_required" &&
    response.invoiceStatus === "not_required"
  );
}

interface ClientInit {
  fetchImpl?: typeof fetch;
  baseUrl?: string;
  /** Explicit, call-local order-first opt-in. Ordinary checkout callers omit this. */
  orderFirst?: boolean;
  /** Selects the existing public Order-first route independently of card markers. */
  publicOrderFirst?: boolean;
  /** Marks the contained operator canary; the API enforces its verified CIAM identity. */
  operatorCanary?: boolean;
}

export function serializeConfirmCheckoutBody(
  request: ConfirmCheckoutRequest,
  neverBlockEnabled?: boolean,
): Record<string, unknown> {
  if (!isValidExpectedQuoteHash(request.expectedQuoteHash)) {
    throw new Error("expectedQuoteHash: invalid (must be hex, ≤128 chars, non-GUID)");
  }
  if (
    request.acceptancePublicId !== undefined &&
    !/^ca_[A-Za-z0-9_-]+$/.test(request.acceptancePublicId)
  ) {
    throw new Error("acceptancePublicId: invalid public id");
  }
  return appendCheckoutFlowVersion(
    request.acceptancePublicId === undefined
      ? { expectedQuoteHash: request.expectedQuoteHash.trim() }
      : {
          expectedQuoteHash: request.expectedQuoteHash.trim(),
          acceptancePublicId: request.acceptancePublicId.trim(),
        },
    neverBlockEnabled,
  );
}

async function readJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function confirmCheckout(
  checkoutSessionPublicId: string,
  request: ConfirmCheckoutRequest,
  init?: ClientInit,
): Promise<ConfirmCheckoutResponse> {
  assertCheckoutMutationAllowed("POST /api/checkout/{cs}/confirm");
  if (!isValidCheckoutSessionPublicId(checkoutSessionPublicId)) {
    throw new Error("checkoutSessionPublicId: invalid public id");
  }
  const orderFirst = init?.orderFirst === true;
  const publicOrderFirst = init?.publicOrderFirst === true;
  const operatorCanary = init?.operatorCanary === true;
  if (orderFirst && publicOrderFirst === operatorCanary) {
    throw new Error("orderFirst requires exactly one route selector.");
  }
  const body = serializeConfirmCheckoutBody(request, orderFirst);
  if (init?.publicOrderFirst === true) {
    if (init.orderFirst !== true) {
      throw new Error("publicOrderFirst requires explicit orderFirst.");
    }
    body.publicOrderFirst = true;
  }
  if (init?.operatorCanary === true) {
    if (init.orderFirst !== true) {
      throw new Error("operatorCanary requires explicit orderFirst.");
    }
    body.operatorCanary = true;
  }
  const base = (init?.baseUrl ?? CHECKOUT_STAGE2_API_BASE_URL).replace(/\/+$/, "");
  const fetchImpl = init?.fetchImpl ?? fetch;
  const res = await fetchImpl(
    `${base}/api/checkout/${encodeURIComponent(checkoutSessionPublicId)}/confirm`,
    {
      method: "POST",
      headers: { Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new CheckoutStage2ApiError(
      res.status,
      parsed,
      `confirmCheckout failed: ${res.status}`,
      res.headers.get("x-correlation-id") || res.headers.get("x-request-id") || null,
    );
  }
  // Retained compatibility client: legacy callers do not opt into the
  // order-first contract. Order-first callers always supply acceptance.
  if (!request.acceptancePublicId) return parsed as ConfirmCheckoutResponse;
  // The order-first API (confirmCheckoutOrderOnly) nests the durable order under
  // `order.orderKey` and the creation counters under `counts`. Validate the
  // correlated order key and the no-invoice guarantee, then surface orderKey at
  // the top level for navigation. NOTE: `counts.orderCreated === false` is the
  // legitimate idempotent-reuse outcome (repeat/refresh confirm returns the same
  // Order), so it must NOT be treated as a failure.
  const value = parsed as Partial<ConfirmCheckoutResponse> & {
    order?: { orderKey?: string } | null;
    counts?: { orderCreated?: boolean } | null;
    payment?: { status?: string } | null;
    commercialState?: string;
    paymentRequired?: boolean;
    invoiceRequired?: boolean;
    paymentStatus?: string;
    invoiceStatus?: string;
  };
  const orderKey = value.order?.orderKey;
  const expectedOrderKey = `order_from_checkout:${checkoutSessionPublicId}`;
  if (value.checkoutSessionPublicId !== checkoutSessionPublicId || orderKey !== expectedOrderKey) {
    throw new Error("Invalid order confirmation response from server.");
  }

  const complimentary =
    value.commercialState === "complimentary" &&
    value.paymentRequired === false &&
    value.invoiceRequired === false &&
    value.invoiceStatus === "not_required" &&
    (value.paymentStatus === "not_required" || value.payment?.status === "not_required");
  if (complimentary) {
    if (
      value.nextAction !== "view_order" &&
      value.nextAction !== "view_access" &&
      value.nextAction !== "wait_for_release" &&
      value.nextAction !== "contact_support" &&
      value.nextAction !== "confirm"
    ) {
      throw new Error("Invalid complimentary confirmation response from server.");
    }
    return {
      ...(value as ComplimentaryConfirmCheckoutResponse),
      orderKey,
      // The public contract permits the nested payment block as an additive
      // mirror, but callers receive one stable discriminant.
      paymentStatus: "not_required",
    };
  }

  if (value.nextAction !== "view_order" || value.invoiceStatus !== "not_created") {
    throw new Error("Invalid order confirmation response from server.");
  }
  return { ...(value as PayableConfirmCheckoutResponse), orderKey };
}
